import { GOOGLE_ANALYTICS_TAG_ID } from 'constants/environment';

import type {
  Alphanumeric,
  Letter,
  OnlyContains,
  StartsWith
} from './stringTypes';

/**
 * List of custom GA events
 * - Append type to add a new custom event type
 */
export type CustomGAEvents = GAEventName<
  | 'logged_in'
  | 'pdf_document_generated'
  | 'pdf_document_preview_error'
  | 'pdf_document_downloaded'
  | 'phone_setup'
  | 'totp_setup'
  | 'debug_test_event'
  // Manual payout
  | 'create_manual_payout_intent'
  | 'create_manual_payout_submitted'
  | 'create_manual_payout_proceeded'
  | 'create_manual_payout_step_changed'
  // Batch upload
  | 'create_batch_intent'
  | 'create_batch_submitted'
  // Approval workflows - payouts / batches
  | 'manual_payout_approval'
  | 'batch_approval'
>;

/**
 * Utility wrapper for dispatching Google Analytics (GA) events
 *
 * Note: It's preferred to use the useGoogleAnalyticsDispatcher hook, as it throttles dispatches
 */
export const dispatchGAEvent = (event: CustomGAEvents, payload?: object) => {
  const { gtag } = window as { gtag?: GTag };

  if (!gtag) {
    if (process.env.DEPLOYMENT_ENV !== 'production') {
      console.warn('Google analytics not configured');
    }
    return;
  }

  return gtag('event', event, payload);
};

/**
 * Utility types
 */
type GTag = (name: 'event', event: CustomGAEvents, payload?: object) => void;

/**
 * Rules for GA event names
 * They are described at: https://support.google.com/analytics/answer/13316687?hl=en#zippy=%2Cweb
 * NOTE: These rules are updated periodically - if an error is displayed, please update this type
 */
type GAEventName<S extends CustomGAEvents> =
  S extends GARuleNameMustStartWithALetter<S> &
    GARuleNameNotReserved<S> &
    GARuleNameOnlyValidCharacters<S>
    ? S
    : never;

type GARuleNameMustStartWithALetter<S extends string> = StartsWith<S, Letter>;

type GARuleNameNotReserved<S extends string> = S extends GAReservedNames
  ? never
  : S;

type GARuleNameOnlyValidCharacters<S extends string> = OnlyContains<
  S,
  GAValidCharacters
>;

// Valid characters
type GAValidCharacters = Alphanumeric | '_';

// Reserved names
type GAReservedNames =
  | 'app_remove'
  | 'app_store_refund'
  | 'app_store_subscription_cancel'
  | 'app_store_subscription_renew'
  | 'click'
  | 'error'
  | 'file_download'
  | 'first_open'
  | 'first_visit'
  | 'form_start'
  | 'form_submit'
  | 'in_app_purchase'
  | 'page_view'
  | 'scroll'
  | 'session_start'
  | 'user_engagement'
  | 'view_complete'
  | 'video_progress'
  | 'video_start'
  | 'view_search_results';

/**
 * Dispatch user login / logout
 */

type GTagConfig = (name: 'config', tag: string, payload?: object) => void;
export const configUserLogin = (userId: string) => {
  const { gtag } = window as { gtag?: GTagConfig };

  if (!gtag || !GOOGLE_ANALYTICS_TAG_ID) {
    return;
  }

  gtag('config', GOOGLE_ANALYTICS_TAG_ID, {
    user_id: userId,
    send_page_view: false
  });
};

type GTagSet = (name: 'set', payload?: object) => void;
export const configUserLogout = () => {
  const { gtag } = window as { gtag?: GTagSet };

  if (!gtag) {
    return;
  }

  gtag('set', { user_id: null });
};
